/**
 * build-precision-prompt.js
 * Domain logic for generating PRECISION prompts for the Coach use case.
 */
export function buildPrecisionPrompt({
  userInput,
  persona = 'Agile Coach',
  lens = 'general',
  teamName = null,
  teamId = null,
  files = [],
  teamInstructions = null,
  teamType = null,
  usePrecision = true,
}) {
  if (!usePrecision) {
    return userInput;
  }

  const attachmentInfo = formatAttachments(files);
  const teamInfo = formatTeamInstructions(teamInstructions);
  const teamLabel = resolveTeamRef(teamName, teamId);

  return [
    `**PERSONA**: You are an expert agile coach with deep experience in ${persona} coaching approach.`,
    '',
    `**REASONING**: The user is asking about: "${userInput}"`,
    `Context: ${lens !== 'auto' ? `Using ${lens} lens for analysis. ` : ''}${teamLabel ? `Team context: ${teamLabel}. ` : ''}`.trim(),
    '',
    '**EXPECTATION**: Provide practical, actionable agile coaching advice that:',
    '- Addresses the specific situation described',
    '- Offers concrete next steps the user can take',
    '- Includes relevant frameworks, practices, or techniques',
    `- Considers team dynamics and organizational context${files.length ? '\n- Analyzes attached files and incorporates specific insights from them' : ''}`.trim(),
    '',
    `**CLARIFICATION**: Focus on coaching guidance rather than just theoretical knowledge. Help the user understand both the "what" and the "how" of implementation.${files.length ? ' The user has provided files that should be analyzed for data-driven insights.' : ''}`.trim(),
    '',
    `**INSTRUCTION**: Based on the user's question: "${userInput}"`,
    '',
    'Please provide comprehensive coaching advice that includes:',
    '1. **Immediate Actions**: What can they start doing today?',
    '2. **Framework Application**: Which agile practices/frameworks apply?',
    '3. **Common Pitfalls**: What should they watch out for?',
    `4. **Success Metrics**: How will they know it's working?${files.length ? '\n5. **File Analysis**: Specific insights from attached files' : ''}`.trim(),
    '',
    '**SCOPE**: Keep the response focused on practical coaching for the specific question asked, avoiding generic advice.',
    '',
    '**INFORMATION**: ',
    `User Question: ${userInput}`,
    `Coaching Context: ${persona} approach via ${lens} lens`,
    teamLabel ? `Team: ${teamLabel}` : 'No specific team context',
    attachmentInfo,
    teamInfo,
    '',
    '**OUTPUT**: Use standard markdown formatting:',
    '- ## for headers  ',
    '- **bold** for emphasis',
    '- "- " for EVERY bullet point (never forget the dash and space)',
    '- Blank lines between sections, no blank lines between bullets in same list',
    '',
    '**NEXT**: Provide your coaching response in proper markdown format.',
  ]
    .filter(Boolean)
    .join('\n');
}

function formatAttachments(files) {
  if (!files || !files.length) return '';
  const detail = files
    .map((file) => {
      const sizeKB = file.size ? Math.round(file.size / 1024) : 0;
      return `${file.name} (${file.type || 'file'}, ${sizeKB}KB)`;
    })
    .join(', ');
  return `- Attached Files: ${detail}`;
}

function resolveTeamRef(name, id) {
  const candidate = typeof name === 'string' && name.trim() ? name.trim() : '';
  if (candidate) return candidate;
  return typeof id === 'string' && id.trim() ? id.trim() : '';
}

function formatTeamInstructions(instructions) {
  if (!instructions) return '';
  return `- Team-Specific Instructions: ${instructions}`;
}

// Support legacy modules that still read from window.AgileProDomain
if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.coach = window.AgileProDomain.coach || {};
  window.AgileProDomain.coach.buildPrecisionPrompt = buildPrecisionPrompt;
}
